/**
 * error-boundary.tsx — where a failure to draw stops.
 *
 * Whatever throws while React draws it is taken off the screen up to the
 * nearest boundary, which draws its `fallback` in its place: at the root the
 * whole game's sheet on the ground (crash-sheet.tsx); around the section the
 * page's sheet in the main column, the shell running on
 * (game/common/page-problem.tsx); and for each of the match's moments and its
 * team's activity nothing at all, so a broken one closes and the game carries
 * on (game/match-overlays.tsx). Before this a render error emptied the root
 * and left the browser's dark canvas, with no words and nothing to press.
 *
 * `onFailed` is told once the failure is caught, so what closed can be closed
 * where it is kept too: a moment the server waits on is let go in its domain,
 * as its own Continue would. `resetKey` draws the children again once it
 * changes — the next moment in a queue, the next phase of an activity —
 * where the boundary otherwise stays on its fallback until it is unmounted.
 *
 * What it knows goes to the console — the error, and where in the tree it was
 * thrown — and the sheets show the error's message, folded under Details
 * (problem-sheet.tsx). The root also sends a scrubbed report through its
 * onFailed callback. A boundary is a
 * class component: React gives a function component no way to catch.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** What it guards, as the console names it: "the game", "the fleet section". */
  name: string;
  /** What stands in the place of what could not be drawn, given what it threw. */
  fallback: (error: unknown) => ReactNode;
  /** Told once the failure is caught: what closed is closed where it is kept too. */
  onFailed?: (error: unknown, info: ErrorInfo) => void;
  /** What it draws now; a change draws the children again after a failure. */
  resetKey?: string | number | null;
  children: ReactNode;
}

interface ErrorBoundaryState {
  /** Something under it threw while it was drawn; `error` is what. */
  failed: boolean;
  error: unknown;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { failed: true, error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`[ErrorBoundary] ${this.props.name} could not be drawn:`, error, info.componentStack);
    this.props.onFailed?.(error, info);
  }

  componentDidUpdate(previous: ErrorBoundaryProps) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false, error: null });
  }

  render() {
    return this.state.failed ? this.props.fallback(this.state.error) : this.props.children;
  }
}
